import React, { useRef, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { PaymentIcon } from 'modules/ui/atoms/Icon'
import InlineCreditCardInputs, { InlineCreditCardInputsRef } from './inline-credit-card-inputs'
import { setCreditCardFormRef } from './credit-card-action-button'
import DonorInfoFields, { DonorInfoFormData } from './donor-info-fields'
import AddressFields from './address-fields'
import {
  makeDonorInfoValidationSchema,
  makeDonorInfoDefaultValues,
} from '../utils/donor-info-validation'
import {
  registerForm,
  unregisterForm,
} from '../../steps/single/utils/single-step-form-orchestrator'
import useOrder from '../../../../order/state/order/use-order'
import useConfig from '../../../../config/state/use-config'

const AccordionWrapper = styled.div`
  padding: 8px 0 0 0;
`

const InlineCardIcons = styled.div`
  display: flex;
  gap: 2px;
`

// Simple component that just shows card icons (for secondaryItems)
export const CreditCardSecondaryItems = () => {
  return (
    <InlineCardIcons className="card-icons">
      <PaymentIcon name="visa" />
      <PaymentIcon name="amex" />
      <PaymentIcon name="discover" />
      <PaymentIcon name="mastercard" />
    </InlineCardIcons>
  )
}

// Component that shows the full credit card form (for accordionContent)
export const CreditCardAccordionContent = () => {
  const order = useOrder()
  const config = useConfig()
  const creditCardFormRef = useRef<InlineCreditCardInputsRef>(null)

  const [isLoading, setIsLoading] = useState(false)

  // Set up form for donor info fields
  const form = useForm<DonorInfoFormData>({
    defaultValues: makeDonorInfoDefaultValues(config.isBillingAddressRequired),
    resolver: yupResolver(makeDonorInfoValidationSchema(config.isBillingAddressRequired)),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  const handleCardBrand = (brand: string) => {
    // stripe uses 'amex', tokenex uses 'americanExpress'
    order.paymentDetails.setIsAmex(brand === 'amex' || brand === 'americanExpress')
  }

  const handleLoadingChange = (loading: boolean) => {
    setIsLoading(loading)
  }

  // Register the credit card ref globally so the action button can access it
  useEffect(() => {
    setCreditCardFormRef(creditCardFormRef)
  }, [])

  // Register forms with the orchestrator
  useEffect(() => {
    registerForm({
      name: 'creditCardDonorInfo',
      formRef: form,
      errorMessage: 'Please fill in all required donor information fields',
    })

    return () => {
      unregisterForm({ name: 'creditCardDonorInfo' })
    }
  }, [form])

  return (
    <FormProvider {...form}>
      <AccordionWrapper>
        {/* Donor Info Fields: Email, First Name, Last Name */}
        <DonorInfoFields isLoading={isLoading} />

        {/* Credit Card Fields: Card Number, Expiry, CVC */}
        <InlineCreditCardInputs
          ref={creditCardFormRef}
          onCardBrand={handleCardBrand}
          onLoadingChange={handleLoadingChange}
        />

        {/* Country, Zip Code - Only show when full billing address is NOT required */}
        {!config.isBillingAddressRequired && <AddressFields isLoading={isLoading} />}
      </AccordionWrapper>
    </FormProvider>
  )
}

CreditCardAccordionContent.displayName = 'CreditCardAccordionContent'
